import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMedplum } from "@medplum/react";
import type { Practitioner, PractitionerRole } from "@medplum/fhirtypes";
import { CARE_TEAM_BATCH_SMALL_COUNT } from "@benchmark/core/constants";
import { DomainEventNames } from "@benchmark/core/events";
import { useEventDrivenRefetch } from "@benchmark/core/hooks";

/**
 * Result type for usePatientAssignedClinician hook.
 * @agentic @benchmark/core
 */
export type UsePatientAssignedClinicianResult = {
  practitionerRole: PractitionerRole | undefined;
  practitioner: Practitioner | undefined;
  /** Readiness of the CareTeam/PractitionerRole assignment and its sending numbers. */
  isLoading: boolean;
  error: Error | null;
  /** Independent readiness of the linked Practitioner used for template names. */
  isLoadingPractitioner: boolean;
  practitionerError: Error | null;
  refetch: () => Promise<void>;
};

/**
 * Fetches the first role from the first active CareTeam, then enriches its name without blocking role-based messaging.
 * @agentic @benchmark/core
 */
export function usePatientAssignedClinician(
  patientId: string | undefined,
  { enabled = true }: { enabled?: boolean } = {},
): UsePatientAssignedClinicianResult {
  const medplum = useMedplum();
  // A distinct scope also invalidates the prior result on reopening the same patient.
  const scope = useMemo(
    () => ({ patientId, enabled, medplum }),
    [patientId, enabled, medplum],
  );
  const [state, setState] = useState<{
    scope: typeof scope;
    practitionerRole: PractitionerRole | undefined;
    practitioner: Practitioner | undefined;
    isLoading: boolean;
    error: Error | null;
    isLoadingPractitioner: boolean;
    practitionerError: Error | null;
  }>({
    scope,
    practitionerRole: undefined,
    practitioner: undefined,
    isLoading: !!patientId && enabled,
    error: null,
    isLoadingPractitioner: false,
    practitionerError: null,
  });
  const requestRef = useRef<{
    generation: number;
    controller?: AbortController;
  }>({ generation: 0 });
  const activeScopeRef = useRef<typeof scope | null>(null);

  const refetch = useCallback(async (): Promise<void> => {
    if (!scope.patientId || !scope.enabled || activeScopeRef.current !== scope)
      return;

    const request = requestRef.current;
    request.controller?.abort();
    const generation = ++request.generation;
    const controller = new AbortController();
    request.controller = controller;
    const isCurrent = () =>
      !controller.signal.aborted && request.generation === generation;

    setState({
      scope,
      practitionerRole: undefined,
      practitioner: undefined,
      isLoading: true,
      error: null,
      isLoadingPractitioner: false,
      practitionerError: null,
    });
    try {
      // Reload bypasses Medplum's request cache for the search and both linked resources.
      const options = { cache: "reload" as const, signal: controller.signal };
      const careTeams = await scope.medplum.searchResources(
        "CareTeam",
        {
          subject: `Patient/${scope.patientId}`,
          status: "active",
          _count: CARE_TEAM_BATCH_SMALL_COUNT.toString(),
        },
        options,
      );
      if (!isCurrent()) return;

      const reference = careTeams[0]?.participant?.find((participant) =>
        participant.member?.reference?.startsWith("PractitionerRole/"),
      )?.member;
      const resource = reference
        ? await scope.medplum.readReference(reference, options)
        : undefined;
      if (!isCurrent()) return;
      if (resource && resource.resourceType !== "PractitionerRole") {
        throw new Error(
          "Assigned clinician lookup returned an unexpected resource",
        );
      }
      // A fresh role establishes valid sending numbers independently of whether
      // its optional linked name can be read. Never carry a prior name forward.
      const practitionerReference = resource?.practitioner;
      setState({
        scope,
        practitionerRole: resource,
        practitioner: undefined,
        isLoading: false,
        error: null,
        isLoadingPractitioner: !!practitionerReference?.reference,
        practitionerError: null,
      });
      if (!practitionerReference?.reference) return;

      try {
        const practitioner = await scope.medplum.readReference(
          practitionerReference,
          options,
        );
        if (!isCurrent()) return;
        if (practitioner.resourceType !== "Practitioner") {
          throw new Error(
            "Assigned practitioner lookup returned an unexpected resource",
          );
        }
        setState({
          scope,
          practitionerRole: resource,
          practitioner,
          isLoading: false,
          error: null,
          isLoadingPractitioner: false,
          practitionerError: null,
        });
      } catch (error) {
        if (!isCurrent()) return;
        // Name enrichment cannot revoke a successfully validated current role.
        // Consumers still distinguish this failure from a confirmed missing name.
        setState({
          scope,
          practitionerRole: resource,
          practitioner: undefined,
          isLoading: false,
          error: null,
          isLoadingPractitioner: false,
          practitionerError:
            error instanceof Error
              ? error
              : new Error("Unable to load assigned practitioner"),
        });
      }
    } catch (error) {
      if (!isCurrent()) return;
      // A failed read (including a deleted role) is not a confirmed missing assignment.
      // Do not log patient-scoped request errors, which can include resource data.
      setState({
        scope,
        practitionerRole: undefined,
        practitioner: undefined,
        isLoading: false,
        error:
          error instanceof Error
            ? error
            : new Error("Unable to load assigned clinician"),
        isLoadingPractitioner: false,
        practitionerError: null,
      });
    } finally {
      if (isCurrent()) request.controller = undefined;
    }
  }, [scope]);

  useEffect(() => {
    activeScopeRef.current = scope;
    const request = requestRef.current;
    let active = true;
    queueMicrotask(() => {
      if (active) void refetch();
    });
    return () => {
      active = false;
      activeScopeRef.current = null;
      request.generation++;
      request.controller?.abort();
    };
  }, [refetch, scope]);

  useEffect(() => {
    if (!enabled || !patientId) return;
    const handleFocus = () => {
      void refetch();
    };
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [enabled, patientId, refetch]);

  useEventDrivenRefetch(
    [
      DomainEventNames.PatientUpdated,
      DomainEventNames.CareTeamCreated,
      DomainEventNames.CareTeamUpdated,
    ],
    {
      refetch,
      enabled: enabled && !!patientId,
      filter: (payload) =>
        (payload as { patientId?: string }).patientId === patientId,
    },
  );

  const current = enabled && !!patientId && state.scope === scope;
  return {
    practitionerRole: current ? state.practitionerRole : undefined,
    practitioner: current ? state.practitioner : undefined,
    isLoading: enabled && !!patientId && (!current || state.isLoading),
    error: current ? state.error : null,
    isLoadingPractitioner: current && state.isLoadingPractitioner,
    practitionerError: current ? state.practitionerError : null,
    refetch,
  };
}
